import { isMarketDay } from '../config/constants'

/**
 * Date Utility Functions
 * Shared across API and Frontend
 */

/**
 * Parse DD-MMM-YYYY to Date object
 * e.g. 09-Dec-2025 -> Date object
 */
export function parseDateStr(dateStr: string): Date | null {
    try {
        if (!dateStr) return null
        // Clean the string: remove quotes, trim
        const cleanStr = dateStr.replace(/['"]/g, '').trim()

        // Handle '-', ' ', or '/' separator and filter empty results
        const parts = cleanStr.split(/[- /]/).filter(Boolean)
        if (parts.length < 3) return null

        // Only use the first 3 parts (Date part: DD-MMM-YYYY or similar)
        // Ignoring any time components like HH:mm:ss
        let day: number, month: number, year: number

        const p0 = parseInt(parts[0] || '0', 10)
        const p1Raw = parts[1] || '0'
        const p2 = parseInt(parts[2] || '0', 10)

        // Check if middle part is text (e.g. "Dec")
        const months: Record<string, number> = {
            jan: 0,
            feb: 1,
            mar: 2,
            apr: 3,
            may: 4,
            jun: 5,
            jul: 6,
            aug: 7,
            sep: 8,
            oct: 9,
            nov: 10,
            dec: 11,
        }

        if (Number.isNaN(parseInt(p1Raw, 10))) {
            // Case 1: DD-MMM-YYYY (e.g. 05-Dec-2025)
            day = p0
            year = p2
            const mStr = p1Raw.toLowerCase()
            if (months[mStr] === undefined) return null
            month = months[mStr]
        } else {
            // All Numeric Case: Ambiguity check
            const p1 = parseInt(p1Raw, 10)

            // Special Check for DD-MM-YYYY (Common in Indian CSVs)
            // e.g. 24-12-2025: p0=24, p1=12, p2=2025
            if (p0 > 12 && p1 <= 12 && p2 > 31) {
                day = p0
                month = p1 - 1
                year = p2
            } else {
                // Heuristic Fallback
                // If p0 > 12, it MUST be day -> DD/MM/YYYY
                // If p1 > 12, it MUST be day -> MM/DD/YYYY
                // If both <= 12, prefer DD/MM/YYYY (Indian Standard)

                if (p0 > 31) {
                    // YYYY-MM-DD (ISO-like)
                    year = p0
                    month = p1 - 1
                    day = p2
                } else if (p0 > 12) {
                    // Definitely DD/MM
                    day = p0
                    month = p1 - 1
                    year = p2
                } else if (p1 > 12) {
                    // Definitely MM/DD
                    month = p0 - 1
                    day = p1
                    year = p2
                } else {
                    // Ambiguous (e.g. 05/06/2025). Default to DD/MM/YYYY
                    day = p0
                    month = p1 - 1
                    year = p2
                }
            }

            if (month < 0 || month > 11) return null

            // Year correction (handle 2-digit years if needed, though unlikely for CSV)
            if (year < 100) year += 2000
        }

        return new Date(year, month, day)
    } catch (_e) {
        return null
    }
}

/**
 * Format Date object to DD-MM-YYYY (IST)
 */
export function formatDateToDDMMYYYY(date: Date): string {
    if (!date || isNaN(date.getTime())) return '-'
    try {
        return date
            .toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                timeZone: 'Asia/Kolkata',
            })
            .replace(/\//g, '-') // ensure hyphen separator
    } catch {
        // Fallback for environments without ICU
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        return `${day}-${month}-${year}`
    }
}

export function formatDateToYYYYMMDD(date: Date): string {
    if (!date || isNaN(date.getTime())) return ''
    try {
        // Only use the part parts to reconstruct YYYY-MM-DD
        const parts = Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: 'Asia/Kolkata',
        }).formatToParts(date)

        const d = parts.find((p) => p.type === 'day')?.value || '01'
        const m = parts.find((p) => p.type === 'month')?.value || '01'
        const y = parts.find((p) => p.type === 'year')?.value || '2025'

        return `${y}-${m}-${d}`
    } catch {
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        return `${year}-${month}-${day}`
    }
}

/**
 * Format timestamp to IST 24-hour time (HH:MM:SS)
 * Ensures consistency across all tables and charts
 */
export function formatISTTime(timestamp: number | string | Date): string {
    if (!timestamp) return '-'
    try {
        let ts = timestamp
        if (typeof ts === 'string' && /^\d+$/.test(ts)) {
            ts = Number(ts)
        }
        const date = new Date(ts)
        if (isNaN(date.getTime())) {
            // Fallback: If date parsing fails (e.g., DD-MM-YYYY format), try to extract HH:mm or HH:mm:ss
            if (typeof ts === 'string' && ts.includes(':')) {
                const timeMatch = ts.match(/\b(\d{1,2}:\d{2}(?::\d{2})?)\b/)
                if (timeMatch && timeMatch[1]) return timeMatch[1]
            }
            return '-'
        }

        return date.toLocaleTimeString('en-IN', {
            timeZone: 'Asia/Kolkata',
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        })
    } catch {
        return '-'
    }
}

/**
 * Get the last valid trading day (Mon-Fri)
 * If today is a trading day, returns today.
 * If today is Sat/Sun, returns Friday.
 */
export function getLastTradingDay(fromDate?: Date): Date {
    const d = fromDate ? new Date(fromDate) : new Date()
    // Safety loop (max 7 days to find a market day)
    for (let i = 0; i < 7; i++) {
        if (isMarketDay(d)) return d
        d.setDate(d.getDate() - 1)
    }
    return d // Fallback
}

/**
 * Safely parse date & time strings (including DD-MM-YYYY formats commonly found in Indian sheets/live data)
 * to numeric milliseconds timestamp.
 */
export function parseDateAndTimeToMs(val: any): number {
    if (!val) return 0
    if (typeof val === 'number') return val
    if (val instanceof Date) return val.getTime()
    
    if (typeof val === 'string') {
        const trimmed = val.replace(/['"]/g, '').trim()
        const num = Number(trimmed)
        if (!isNaN(num) && trimmed !== '') return num
        
        // Handle pure time strings like "09:10" or "09:10:00"
        if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
            const timeParts = trimmed.split(':')
            const hour = parseInt(timeParts[0] || '0', 10)
            const minute = parseInt(timeParts[1] || '0', 10)
            const second = parseInt(timeParts[2] || '0', 10)
            const d = new Date()
            d.setHours(hour, minute, second, 0)
            return d.getTime()
        }
        
        // Handle DD-MM-YYYY HH:mm:ss format or DD/MM/YYYY HH:mm:ss
        // Example: "25-05-2026 09:10:02" or "25/05/2026 15:30:00"
        const parts = trimmed.split(/[\sT]+/)
        if (parts[0]) {
            const dateParts = parts[0].split(/[-/]/)
            if (dateParts.length === 3) {
                let day = 1, month = 0, year = 1970
                const p0 = parseInt(dateParts[0] || '0', 10)
                const p1 = parseInt(dateParts[1] || '0', 10)
                const p2 = parseInt(dateParts[2] || '0', 10)
                
                if (p0 > 31) {
                    // YYYY-MM-DD
                    year = p0
                    month = p1 - 1
                    day = p2
                } else {
                    // DD-MM-YYYY
                    day = p0
                    month = p1 - 1
                    year = p2
                }
                
                let hour = 0, minute = 0, second = 0
                if (parts[1]) {
                    const timeParts = parts[1].split(':')
                    hour = parseInt(timeParts[0] || '0', 10)
                    minute = parseInt(timeParts[1] || '0', 10)
                    second = parseInt(timeParts[2] || '0', 10)
                }
                
                const d = new Date(year, month, day, hour, minute, second)
                const t = d.getTime()
                if (!isNaN(t)) return t
            }
        }
        
        const fallback = new Date(trimmed).getTime()
        return isNaN(fallback) ? 0 : fallback
    }
    return 0
}
